import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

interface IPwaInstallerContext {
  promptRef?: BeforeInstallPromptEvent;
  isInstalled: boolean;
  isBrowserSupported: boolean;
  handleInstall: () => Promise<void>;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PwaInstallerContext = createContext<IPwaInstallerContext>({
  promptRef: undefined,
  isInstalled: false,
  isBrowserSupported: false,
  handleInstall: async () => {},
});

export function PwaInstallerContextProvider({ children }: { children: ReactNode }) {
  const [promptRef, setPromptRef] = useState<BeforeInstallPromptEvent>();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Check if we already have a deferred prompt
    if (window.deferredPrompt) {
      setPromptRef(window.deferredPrompt);
      setDeferredPrompt(window.deferredPrompt);
      window.deferredPrompt = null;
    }

    const handler = (e: BeforeInstallPromptEvent) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setPromptRef(e);
      setDeferredPrompt(e);
      window.deferredPrompt = e;
    };

    window.addEventListener('beforeinstallprompt', handler as EventListener);
    return () => window.removeEventListener('beforeinstallprompt', handler as EventListener);
  }, []);

  useEffect(() => {
    const handler = () => {
      setPromptRef(undefined);
      setDeferredPrompt(null);
      window.deferredPrompt = null;
    };

    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  const isInstalled = useMemo(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    return false;
  }, []);

  const isBrowserSupported = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    //? Arc browser has same user agent as chrome but has a custom css variable so this is the only way to detect it
    const isArc = getComputedStyle(document.documentElement).getPropertyValue('--arc-palette-title') !== '';
    const isChrome = userAgent.includes('chrome') && !isArc;
    const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');
    const isEdge = userAgent.includes('edg');

    // Arc browser does not support PWA installation
    if (isArc) return false;
    // Chrome supports PWA installation
    if (isChrome || isEdge) return true;
    // Safari on iOS supports "Add to Home Screen"
    if (isSafari && /iphone|ipad|ipod/.test(userAgent)) return true;

    return false;
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;

      if (result.outcome === 'accepted') {
        setDeferredPrompt(null);
        window.deferredPrompt = null;
      }
    } catch (err) {
      console.error('Error installing PWA:', err);
    }
  };

  return (
    <PwaInstallerContext.Provider value={{ isInstalled, promptRef, isBrowserSupported, handleInstall }}>
      {children}
    </PwaInstallerContext.Provider>
  );
}

export function usePwaInstaller() {
  const context = useContext(PwaInstallerContext);
  if (context === undefined) {
    throw new Error('usePwaInstaller must be used within a PwaInstallerContextProvider');
  }
  return context;
}

// Add this to the global Window interface
declare global {
  interface Window {
    deferredPrompt: BeforeInstallPromptEvent | null;
  }
}
